/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {InfoPopUpComponent} from '../../../core/components/pop-ups/info-pop-up/info-pop-up.component';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-propulsion-template-save',
    templateUrl: './propulsion-template-save.component.html',
    styleUrl: './propulsion-template-save.component.scss',
    standalone: false
})
export class PropulsionTemplateSaveComponent {
  showSpinner: boolean = false;
  file: File;
  sheets: string[];
  sheetNames: string[];
  propulsionScenario: any;


  constructor(public snackBar: FabMessageService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              public stateService: StateService,
              public dialog: MatDialog,
              public readonly excelService: ExcelExportService,
              readonly router: Router) {
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: true
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheetNames = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheetNames.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.verifyTemplateSave();
        }
      }
    );
  }

  /**
   * Shows a dialog to confirm the query save
   */
  async verifyTemplateSave(): Promise<void> {
    this.showSpinner = true;
    try {
      this.propulsionScenario = await this.propulsionScenarioService.getScenarioFromTemplate(this.file, this.sheetNames).toPromise();
      const duplicates: any [] = await this.getPropulsionDuplicates(this.propulsionScenario.id);

      if (duplicates.length > 0) {
        this.snackBar.displayError('This scenario contains duplicate records. Contact Central Office Admin');
        this.showSpinner = false;
        return
      }
      this.showConfirmationDialog();
    } catch (error) {
      this.snackBar.displayError("Save Aborted: " + error.message, DISMISS_MSG);
    } finally {
      this.showSpinner = false;
    }
  }

  /**
   * Get duplicate records of the specified Propulsion Scenario
   */
  async getPropulsionDuplicates(propScenId: number) {
    try {
      const duplicatePromise = this.propulsionScenarioService.getPropDupeRecords(propScenId).toPromise();
      return await duplicatePromise;
    } catch {
      throw (new Error("Error checking for duplicates"));
    }
  }

  /**
   * Shows a dialog for confirming the save for the specific scenario
   */
  showConfirmationDialog() {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Template Save Confirmation',
        message: 'Do you wish to save data back to scenario ' + this.propulsionScenario.id + ': "' + this.propulsionScenario.propulsionScenarioName + '"?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });
    dialogRef.componentInstance.yes.subscribe(async () => {
      await this.saveForecastTemplate();
    });
    dialogRef.componentInstance.no.subscribe(() => {
      this.propulsionScenario = null;
      dialogRef.close();
    });
  }

  /**
   * Saves the forecast template, displaying errors if any are found in the template
   */
  async saveForecastTemplate() {
    this.showSpinner = true;
    try {
      await this.propulsionScenarioService.saveForecastTemplate(this.file, this.sheetNames, this.stateService.getUserGmid()).toPromise();
      this.showSpinner = false;
      this.showSuccessfulSaveDialog();
    } catch (error) {
      this.showSpinner = false;
      if (error.includes('penetration sum')) {
        let info = error.replaceAll(', ', ',\n');
        info = info.replaceAll(': ', ':\n');
        this.showTemplateErrors(info);
      } else {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    }
  }

  /**
   * Shows a pop-up for the incorrect sums
   * @param info the string to show in the pop-up
   */
  showTemplateErrors(info: string): void {
    this.dialog.open(InfoPopUpComponent, {
      data: {
        title: 'Template Errors',
        info
      }
    });
  }

  /**
   * Shows a confirmation dialog after a successful save that allows the user to select the updated scenario
   */
  showSuccessfulSaveDialog() {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Scenario Successfully Updated',
        message: 'Would you like to return to the home menu?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });
    dialogRef.componentInstance.yes.subscribe(() => {
      dialogRef.close();
      this.router.navigate(['/']);
    });
    dialogRef.componentInstance.no.subscribe(() => {
      this.propulsionScenario = null;
      dialogRef.close();
    });
  }
}
